'use client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  clearAuthCache,
  clearPWACache,
  clearVersionCache,
  type ClearCacheResult,
} from '@/utils/pwaCache';
import { AlertCircle, CheckCircle, RotateCcw, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

interface ClearCacheButtonProps {
  className?: string;
  variant?: 'icon' | 'button';
  position?: 'fixed' | 'relative';
}

export const ClearCacheButton: React.FC<ClearCacheButtonProps> = ({
  className = '',
  variant = 'icon',
  position = 'fixed',
}) => {
  const [isClearing, setIsClearing] = useState(false);
  const [lastResult, setLastResult] = useState<ClearCacheResult | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Only show in development environment
  if (process.env.NODE_ENV === 'production') {
    return null;
  }

  const handleClearPWACache = async () => {
    setIsClearing(true);
    try {
      const result = await clearPWACache();
      setLastResult(result);
      setShowDetails(true);

      if (result.success) {
        // Auto-refresh after a short delay to show the success message
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      setLastResult({
        success: false,
        message: `Unexpected error: ${error}`,
      });
      setShowDetails(true);
    } finally {
      setIsClearing(false);
    }
  };

  const handleClearAuthCache = () => {
    setIsClearing(true);
    try {
      const result = clearAuthCache();
      setLastResult(result);
      setShowDetails(true);

      if (result.success) {
        // Auto-refresh after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      setLastResult({
        success: false,
        message: `Unexpected error: ${error}`,
      });
      setShowDetails(true);
    } finally {
      setIsClearing(false);
    }
  };

  const handleClearVersionCache = () => {
    setIsClearing(true);
    try {
      const result = clearVersionCache();
      setLastResult(result);
      setShowDetails(true);

      if (result.success) {
        // Auto-refresh after a short delay
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      setLastResult({
        success: false,
        message: `Unexpected error: ${error}`,
      });
      setShowDetails(true);
    } finally {
      setIsClearing(false);
    }
  };

  const baseClasses = position === 'fixed' ? 'fixed bottom-0.5 right-0.5 z-50 shadow-lg z-50' : '';

  if (variant === 'button') {
    return (
      <div className={`${baseClasses} ${className}`}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" disabled={isClearing}>
              {isClearing ? (
                <RotateCcw className="h-2 w-2 animate-spin" />
              ) : (
                <Trash2 className="h-2 w-2" />
              )}
              Clear Cache
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={handleClearVersionCache} disabled={isClearing}>
              <RotateCcw className="mr-2 h-2 w-2" />
              Clear Version Cache
              <Badge variant="secondary" className="ml-auto text-xs">
                Safe
              </Badge>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleClearAuthCache} disabled={isClearing}>
              <AlertCircle className="mr-2 h-2 w-2" />
              Clear Auth Cache
              <Badge variant="secondary" className="ml-auto text-xs">
                Safe
              </Badge>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleClearPWACache} disabled={isClearing}>
              <Trash2 className="mr-2 h-2 w-2" />
              Clear All PWA Cache
              <Badge variant="destructive" className="ml-auto text-xs">
                Full
              </Badge>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Result notification */}
        {showDetails && lastResult && (
          <div
            className={`rounded-md p-1 text-sm ${
              lastResult.success
                ? 'border border-green-200 bg-green-50 text-green-800'
                : 'border border-red-200 bg-red-50 text-red-800'
            }`}
          >
            <div className="flex items-center gap-2">
              {lastResult.success ? (
                <CheckCircle className="h-2 w-2" />
              ) : (
                <AlertCircle className="h-2 w-2" />
              )}
              <span className="font-medium">{lastResult.message}</span>
            </div>
            {lastResult.details && lastResult.details.length > 0 && (
              <ul className="mt-2 text-xs opacity-75">
                {lastResult.details.slice(0, 3).map((detail, index) => (
                  <li key={index}>• {detail}</li>
                ))}
                {lastResult.details.length > 3 && (
                  <li>• ... and {lastResult.details.length - 3} more</li>
                )}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  }

  // Icon variant (default)
  return (
    <div className={`${baseClasses} ${className}`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            disabled={isClearing}
            className="bg-background/80 hover:bg-background/90 h-6 w-6 rounded-full border-none text-green-800 backdrop-blur-sm"
            title="Clear Cache"
          >
            {isClearing ? (
              <RotateCcw className="h-2 w-2 animate-spin" />
            ) : (
              <Trash2 className="h-2 w-2" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={handleClearVersionCache} disabled={isClearing}>
            <RotateCcw className="mr-2 h-2 w-2" />
            Clear Version Cache
            <Badge variant="secondary" className="ml-auto text-xs">
              Safe
            </Badge>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleClearAuthCache} disabled={isClearing}>
            <AlertCircle className="mr-2 h-2 w-2" />
            Clear Auth Cache
            <Badge variant="secondary" className="ml-auto text-xs">
              Safe
            </Badge>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleClearPWACache} disabled={isClearing}>
            <Trash2 className="mr-2 h-2 w-2" />
            Clear All PWA Cache
            <Badge variant="destructive" className="ml-auto text-xs">
              Full
            </Badge>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Floating result notification */}
      {showDetails && lastResult && (
        <div
          className={`absolute top-12 right-0 w-80 rounded-md p-3 text-sm shadow-lg ${
            lastResult.success
              ? 'border border-green-200 bg-green-50 text-green-800'
              : 'border border-red-200 bg-red-50 text-red-800'
          }`}
        >
          <div className="flex items-center gap-2">
            {lastResult.success ? (
              <CheckCircle className="h-2 w-2" />
            ) : (
              <AlertCircle className="h-2 w-2" />
            )}
            <span className="font-medium">{lastResult.message}</span>
          </div>
          {lastResult.details && lastResult.details.length > 0 && (
            <ul className="mt-2 text-xs opacity-75">
              {lastResult.details.slice(0, 3).map((detail, index) => (
                <li key={index}>• {detail}</li>
              ))}
              {lastResult.details.length > 3 && (
                <li>• ... and {lastResult.details.length - 3} more</li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
